import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { notificationService } from '../../../services/notificationService';

const NotificationSettings = ({ userId }) => {
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);

  const channels = [
    { id: 'email', name: 'Email', icon: 'Mail' },
    { id: 'sms', name: 'SMS', icon: 'MessageSquare' },
    { id: 'whatsapp', name: 'WhatsApp', icon: 'MessageCircle' },
    { id: 'in_app', name: 'In-App', icon: 'Bell' }
  ];

  const notificationTypes = [
    { id: 'lead_assigned', name: 'Lead Assigned', description: 'When a new lead is assigned to you' },
    { id: 'stage_change', name: 'Stage Changes', description: 'When a lead moves to a different stage' },
    { id: 'task_reminder', name: 'Task Reminders', description: 'Reminders for upcoming tasks' },
    { id: 'commission_earned', name: 'Commission Earned', description: 'When you earn a commission' },
    { id: 'system_alert', name: 'System Alerts', description: 'Important system notifications' },
    { id: 'campaign_update', name: 'Campaign Updates', description: 'Marketing campaign notifications' }
  ];

  useEffect(() => {
    loadPreferences();
  }, [userId]);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const prefsData = await notificationService?.getNotificationPreferences(userId);
      setPreferences(prefsData || []);
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const isEnabled = (channel, notificationType) => {
    const pref = preferences?.find(
      p => p?.channel === channel && p?.notificationType === notificationType
    );
    return pref?.enabled !== false;
  };


const handleToggle = async (channel, notificationType, enabled) => {
  try {
    await notificationService?.setNotificationPreference(userId, {
      channel,
      notificationType,
      enabled
    });
    await loadPreferences();
  } catch (error) {
    console.error('Error updating preference:', error);
  }
};


  if (loading) {
    return (
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl p-4 sm:p-6 border border-border">
      <div className="flex items-center mb-6">
        <Icon name="Settings" size={24} className="mr-3 text-primary" />
        <h2 className="text-xl font-semibold text-foreground">Notification Preferences</h2>
      </div>

      <p className="text-sm text-muted-foreground mb-6">
        Choose how you want to receive notifications for different events
      </p>

      <div className="space-y-6">
        {notificationTypes?.map((type) => (
          <div key={type?.id} className="border-b border-border pb-6 last:border-0 last:pb-0">
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-foreground mb-1">{type?.name}</h3>
              <p className="text-xs text-muted-foreground">{type?.description}</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {channels?.map((channel) => (
                <div
                  key={channel?.id}
                  className="flex flex-col items-center p-3 bg-muted/20 rounded-lg"
                >
                  <Icon name={channel?.icon} size={20} className="text-muted-foreground mb-2" />
                  <span className="text-xs text-muted-foreground mb-2">{channel?.name}</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isEnabled(channel?.id, type?.id)}
                      onChange={(e) => handleToggle(channel?.id, type?.id, e?.target?.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NotificationSettings;